import React, { FunctionComponent, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { NavigationContext } from '@context/NavigationContext'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { Choice, OneOfManyQuestion } from '@models/Survey'
import { PredictionData } from '@models/Prediction/Prediction'
import { AnswerType } from '@models/Survey/Answer'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyMode } from '@reducers/SurveyReducer'
import './CitizenForecasting.scss'
import { categoryHasProbabilityForecasting, getCategory } from '@utils/categoryFlow'

export interface OwnProps {}

type Props = OwnProps

const ANOTHER_CHOICE_ID = 665

const parsePartyId = (forecasting: PredictionData | undefined): string | undefined => {
  if (forecasting?.id === ANOTHER_CHOICE_ID) {
    return 'another'
  }

  return String(forecasting?.id)
}

const CitizenForecasting: FunctionComponent<Props> = (): ReactElement => {
  const { t, i18n } = useTranslation('CitizenForecasting')
  const { setPrevHandler, setNextHandler } = useContext(NavigationContext)
  const { survey, setNextStep } = useContext(SurveyContext)
  const { results, citizenForecasting, setCitizenForecasting, submitForecasting, sandboxedResults } =
    useContext(PredictionContext)

  const { setMode } = useContext(SurveyContext)

  useEffect(() => {
    if (!categoryHasProbabilityForecasting(getCategory(survey))) {
      setNextStep(SurveyStep.Results)
    }
  }, [survey])

  const handleNext = useCallback(() => {
    const partyId = parsePartyId(citizenForecasting)

    if (partyId) {
      submitForecasting()
      setNextStep(SurveyStep.Results)
    }
  }, [citizenForecasting])

  useEffect(() => {
    setMode(SurveyMode.RESULTS)
    setPrevHandler(undefined)

    return () => {
      setMode(SurveyMode.DEFAULT)
      setNextHandler(undefined)
      setPrevHandler(undefined)
    }
  }, [])

  useEffect(() => {
    setNextHandler(handleNext)
  }, [citizenForecasting])

  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)
  const choices = useMemo(
    () =>
      ({
        choices: [
          ...(results?.prediction.data ?? []),
          {
            id: ANOTHER_CHOICE_ID,
            value: ANOTHER_CHOICE_ID,
            color: '',
            names: {
              acr: {
                en: 'another',
                fr: 'another',
              },
              full: {
                en: t('choices.another'),
                fr: t('choices.another'),
              },
              short: {
                en: t('choices.another'),
                fr: t('choices.another'),
              },
            },
          },
        ].map(({ id, names: { full } }: PredictionData) => {
          return {
            id: `${id}`,
            text: full[currentLocale],
          } as Choice
        }),
      } as OneOfManyQuestion),
    [currentLocale, results?.prediction.data, t],
  )

  const handleSelection = useCallback(
    (targetIdStr: AnswerType): void => {
      const newTarget = results?.prediction.data.find(({ id }) => id === parseInt(targetIdStr as string))
      if (newTarget) {
        setCitizenForecasting(newTarget)
      }
    },
    [results],
  )

  const tabs = [
    {
      text: t('navigation.title'),
    },
  ]
  return (
    <TabbedPane
      tabs={tabs}
      contentText={t('questionText')}
      choices={choices}
      onChoiceSelection={handleSelection}
      propClassName="CitizenForecasting"
      predictionResults={sandboxedResults ?? results}
    />
  )
}

export default CitizenForecasting
